import { Component, OnInit, ViewChild } from '@angular/core';
import { TrpcService } from '../../services/trpc/trpc.service';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { saveAs } from 'file-saver';

interface WatchdogLog {
    wid: number;
    uid: number;
    type: string;
    message: string;
    variables: string;
    variablesParsed?: any;
    messageInterpolated?: string;
    severity: number;
    severityLabel?: string;
    link: string;
    location: string;
    referer: string | null;
    hostname: string;
    timestamp: number;
    timestampFormatted?: string;
}

interface WatchdogQueryResult {
    logs: WatchdogLog[];
    total: number;
    page: number;
    pageSize: number;
}

@Component({
    selector: 'app-watchdog-logs',
    templateUrl: './watchdog-logs.component.html',
    styleUrls: ['./watchdog-logs.component.scss']
})
export class WatchdogLogsComponent implements OnInit {
    @ViewChild('logsTable') logsTable!: Table;

    logs: WatchdogLog[] = [];
    totalLogs: number = 0;
    isLoading: boolean = false;
    expandedRowKeys: { [key: string]: boolean } = {};

    // Filters
    searchTerm: string = '';
    selectedSeverity: number | null = null;
    selectedType: string = '';
    startDate: Date | null = null;
    endDate: Date | null = null;

    // Dropdown options
    severityOptions: any[] = [
        { label: 'All Severities', value: null },
        { label: 'Emergency', value: 0, severity: 'danger' },
        { label: 'Alert', value: 1, severity: 'danger' },
        { label: 'Critical', value: 2, severity: 'danger' },
        { label: 'Error', value: 3, severity: 'warning' },
        { label: 'Warning', value: 4, severity: 'warning' },
        { label: 'Notice', value: 5, severity: 'info' },
        { label: 'Info', value: 6, severity: 'info' },
        { label: 'Debug', value: 7, severity: 'secondary' }
    ];

    logTypes: string[] = [];
    logTypeOptions: any[] = [];

    // Table settings
    rows: number = 25;
    rowsPerPageOptions: number[] = [10, 25, 50, 100];

    // Store last query params for refresh
    lastQueryParams: any = null;

    constructor(
        private trpcService: TrpcService,
        private messageService: MessageService
    ) {}

    async ngOnInit() {
        await this.loadLogTypes();
    }

    /**
     * Load unique log types for filter dropdown
     */
    async loadLogTypes() {
        try {
            const types = await this.trpcService.trpc.watchdog.getLogTypes.query();
            this.logTypes = types;
            this.logTypeOptions = [
                { label: 'All Types', value: '' },
                ...types.map(type => ({ label: type, value: type }))
            ];
        } catch (error) {
            console.error('Error loading log types:', error);
        }
    }

    /**
     * Get logs with lazy loading (called by PrimeNG table)
     */
    async getLogs(event?: TableLazyLoadEvent) {
        const limit = event?.rows || this.rows;
        const offset = event?.first || 0;
        const sortField = event?.sortField as string || 'timestamp';
        const sortOrder = event?.sortOrder || -1;

        const queryParams = {
            limit,
            offset,
            sortField,
            sortOrder,
            severity: this.selectedSeverity !== null ? this.selectedSeverity : undefined,
            type: this.selectedType || undefined,
            searchTerm: this.searchTerm || undefined,
            startDate: this.startDate ? Math.floor(this.startDate.getTime() / 1000) : undefined,
            endDate: this.endDate ? Math.floor(this.endDate.getTime() / 1000) : undefined
        };

        this.lastQueryParams = queryParams;
        this.isLoading = true;

        try {
            const result: WatchdogQueryResult = await this.trpcService.trpc.watchdog.getWatchdogLogs.query(queryParams);
            this.logs = result.logs;
            this.totalLogs = result.total;
        } catch (error: any) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to load watchdog logs: ${error.message || 'Unknown error'}`
            });
            console.error('Error loading watchdog logs:', error);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Apply filters and reload data
     */
    applyFilters() {
        this.logsTable.reset();
    }

    /**
     * Clear all filters
     */
    clearFilters() {
        this.searchTerm = '';
        this.selectedSeverity = null;
        this.selectedType = '';
        this.startDate = null;
        this.endDate = null;
        this.logsTable.reset();
    }

    /**
     * Export filtered logs as CSV
     */
    async exportLogs() {
        try {
            const exportParams = {
                severity: this.selectedSeverity !== null ? this.selectedSeverity : undefined,
                type: this.selectedType || undefined,
                searchTerm: this.searchTerm || undefined,
                startDate: this.startDate ? Math.floor(this.startDate.getTime() / 1000) : undefined,
                endDate: this.endDate ? Math.floor(this.endDate.getTime() / 1000) : undefined
            };

            const csvContent = await this.trpcService.trpc.watchdog.exportLogs.query(exportParams);

            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });
            const filename = `watchdog-logs-${new Date().toISOString().split('T')[0]}.csv`;
            saveAs(blob, filename);

            this.messageService.add({
                severity: 'success',
                summary: 'Export Successful',
                detail: `Logs exported to ${filename}`
            });
        } catch (error: any) {
            this.messageService.add({
                severity: 'error',
                summary: 'Export Failed',
                detail: error.message || 'Failed to export logs'
            });
            console.error('Error exporting logs:', error);
        }
    }

    /**
     * Format Unix timestamp to local datetime string
     */
    formatTimestamp(timestamp: number): string {
        if (!timestamp) return '';
        const date = new Date(timestamp * 1000);
        return date.toLocaleString();
    }

    /**
     * Get severity badge class
     */
    getSeverityClass(severity: number): "success" | "info" | "warning" | "danger" | "secondary" | "contrast" | undefined {
        const severityMap: { [key: number]: "success" | "info" | "warning" | "danger" | "secondary" | "contrast" } = {
            0: 'danger',    // Emergency
            1: 'danger',    // Alert
            2: 'danger',    // Critical
            3: 'warning',   // Error
            4: 'warning',   // Warning
            5: 'info',      // Notice
            6: 'info',      // Info
            7: 'secondary'  // Debug
        };
        return severityMap[severity] || 'secondary';
    }

    /**
     * Format JSON for display
     */
    formatJson(data: any): string {
        if (!data) return '';
        try {
            return JSON.stringify(data, null, 2);
        } catch (error) {
            return String(data);
        }
    }

    /**
     * Toggle row expansion
     */
    toggleRowExpansion(log: WatchdogLog) {
        const key = String(log.wid);
        if (this.expandedRowKeys[key]) {
            delete this.expandedRowKeys[key];
        } else {
            this.expandedRowKeys[key] = true;
        }
    }

    /**
     * Check if row is expanded
     */
    isRowExpanded(log: WatchdogLog): boolean {
        return !!this.expandedRowKeys[String(log.wid)];
    }

    /**
     * Refresh the current view
     */
    async refresh() {
        if (this.logsTable) {
            await this.getLogs(this.logsTable.createLazyLoadMetadata());
            this.messageService.add({
                severity: 'success',
                summary: 'Refreshed',
                detail: 'Logs refreshed successfully'
            });
        }
    }
}